<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 p-1">
      <h2 class="mb-0 main-title">All Schedules</h2>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-md btn-primary fs-11 fw-normal text-light hover-white"
        @click="$router.push({ name: 'AssignToMeSchedule' })">
          <i class="fa fa-list fs-11 text-light hover-white"></i> My Assignments
        </button>
      </div>
    </div>

    <!-- Filters Bar -->
    <div class="filters-wrapper">
      <div class="filters-bar" id="filtersBar"></div>
      <button @click="resetAllFilters" class="btn btn-sm btn-outline-danger hover-white">
        <span class="material-symbols-outlined fs-6">undo</span> Reset
      </button>
    </div>

    <!-- Dropdown Template -->
    <div id="genericFilterDropdownTemplate" style="display: none;">
      <div class="generic-filter-dropdown">
        <div class="dropdown-header">
          <input type="text" class="filter-search-input" placeholder="Search...">
        </div>
        <div class="dropdown-body filter-options-container"></div>
        <div class="dropdown-footer">
          <button class="filter-clear-btn">Clear</button>
          <button class="filter-apply-btn">Apply</button>
        </div>
      </div>
    </div>

    <div class="card p-1 bg-white">
      <!-- Loading State -->
      <LoadingBar v-if="loading" message="Loading All Schedules..." />

      <!-- Schedules Table -->
      <div v-else class="table-wrapper">
        <ListTable>
          <thead>
            <tr>
              <th @click="sortBy('work_order_name')" class="sortable">
                Work Order <i :class="getSortIcon('work_order_name')"></i>
              </th>
              <th @click="sortBy('schedule_at')" class="sortable">
                Schedule Date <i :class="getSortIcon('schedule_at')"></i>
              </th>
              <th @click="sortBy('status')" class="sortable">
                Status <i :class="getSortIcon('status')"></i>
              </th>
              <th>Run Time Issues</th>
              <th>
                My Role/Task
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="schedule in sortedData" :key="schedule.id">
              <td>
                <div class="mb-0">{{ schedule.get_parent?.get_work_order?.name || 'N/A' }}</div>
              </td>
              <td>
                <span class="">{{ formatDate(schedule.schedule_date) }}</span>
              </td>
              <td>
                <span v-html="getStatusBadge(schedule.status)"></span>
              </td>
              <td>
                <span >
                  {{ getIssuesCount(schedule) }} 
                </span>
                </td>
              <td>
                <span v-for="(role, index) in getMyRoles(schedule)" :key="index" >
                  {{ role }},
                </span>
                <span v-if="getMyRoles(schedule).length === 0" class="text-muted" style="font-style: italic;">No Role</span>
              </td>
              <td>
                <button class="btn btn-sm btn-primary hover-white" @click="startInspection(schedule.id)">

                  <i class="fas fa-play hover-white"></i> {{ schedule.status == null ? 'Open' : 'Open' }}
                </button>
                 <button class="btn btn-sm btn-outline-secondary ms-1 hover-white" @click="$router.push({ name: 'ScheduleReport', params: { id: schedule.id } })">
                  <i class="fa-regular fa-file-lines "></i> Report
                </button>
              </td>
            </tr>
            <tr v-if="schedules.length === 0">
              <td colspan="6" class="text-center py-4">
                <i class="fa fa-folder-open fa-2x text-muted mb-2 d-block"></i>
                <!-- No Schedules Found -->
              </td>
            </tr>
          </tbody>
        </ListTable>
      </div>

      <!-- Pagination -->
      <Pagination :pagination="pagination" @page-change="changePage" />
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import ListTable from '@/components/utils/ListTable.vue'
import { getStatusBadge } from '@/utils/getStatusBadge'
import { getMyRoles } from '@/utils/getMyWORoles'
import { getIssuesCount } from '@/utils/getStatusBadge'

export default {
  name: 'AllSchedules',
  components: { 
    Pagination, 
    LoadingBar, 
    ListTable 
  },
  data() {
    return {
      schedules: [],
      pagination: {
        total_rows: 0,
        per_page: 20,
        current_page: 1,
        total_pages: 1
      },
      loading: false,
      filterManager: null,
      sortColumn: 'schedule_at',
      sortOrder: 'desc',
      currentUserId: null,
      currentFilters: {},
      users: [],
      custom_users: [],
      // Track if filters were applied from URL
      filtersAppliedFromUrl: false,
    }
  },
  computed: {
    sortedData() {
      let result = [...this.schedules];
      
      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];
          
          if (valA === undefined || valA === null) valA = '';
          if (valB === undefined || valB === null) valB = '';
          
          // Handle nested properties like work_order_name
          if (this.sortColumn === 'work_order_name') {
            valA = a.get_parent?.get_work_order?.name || '';
            valB = b.get_parent?.get_work_order?.name || '';
          }
          
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          
          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }
      
      return result;
    }
  },
  mounted() {
    this.currentUserId = localStorage.getItem('user_main_id');
    
    // Check for status filter in URL query params
    const statusParam = this.$route.query.status;
    if (statusParam) {
      // If status is provided in URL, set it as a filter
      this.currentFilters.status = [statusParam];
      this.filtersAppliedFromUrl = true;
    }
    
    // First fetch users, then initialize filters and fetch list
    this.fetchUsersAndInitialize();
  },
  methods: {
    getStatusBadge,
    getMyRoles,
    getIssuesCount,
    
    startInspection(id) {
      this.$router.push({ name: 'ConductSchedule', params: { id } })
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async fetchUsersAndInitialize() {
      await this.fetchUsers();
      // Now initialize filters after users are loaded
      this.$nextTick(() => {
        setTimeout(() => {
          this.initFilters();
          // After filters are initialized, apply the status filter if it came from URL
          if (this.filtersAppliedFromUrl && this.filterManager) {
            const statusParam = this.$route.query.status;
            if (statusParam) {
              // Trigger the filter change to apply the status
              this.filterManager.activeFilters = { status: [statusParam] };
              this.filterManager.updateFilterPillUI(
                this.filterManager.config.filters.find(f => f.key === 'status')
              );
              this.applyFilters({ status: [statusParam] });
            }
          }
        }, 100);
      });
      // Then fetch the list
      this.fetchList();
    },

    async fetchUsers() {
      const result = await form_api_submit({
        url: 'user-vendor/dropdown',
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true,
      });
      if (result.success) {
        this.users = Array.isArray(result.data.users) ? result.data.users : [];
        let vendors = Array.isArray(result.data.vendors) ? result.data.vendors : [];
        // Combine both vendors and users into a single array for filtering
        this.users = [...this.users, ...vendors];
        this.custom_users = (this.users || []).map((user) => ({
          id: user.id,
          name: (user.first_name || '') + " " + (user.last_name || '') + " (" + (user.role == 'vendor' ? 'vendor' : 'internal') + ")",
        }));
      }
    },

    initFilters() {
      if (typeof $ === "undefined" || typeof GenericFilterManager === "undefined") return;

      // Prepare user options for AssignedTo filter
      const userOptions = (this.custom_users || []).map(user => ({
        value: String(user.id),
        label: user.name
      }));

      this.filterManager = new GenericFilterManager({
        containerId: "filtersBar",
        filters: [
          {
            key: "work_order_name",
            label: "Work Order Name",
            type: "input",
            placeholder: "Search by Work Order Name"
          },
          {
            key: "schedule_date",
            label: "Schedule Date",
            type: "input",
            placeholder: "Select Date",
            inputType: "date"
          },
          {
            key: "status",
            label: "Status",
            type: "checkbox",
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'started', label: 'Started' },
              { value: 'under_action', label: 'Under Action' },
              { value: 'under_verification', label: 'Under Verification' },
              { value: 'completed', label: 'Completed' },
              { value: 'on_hold', label: 'On Hold' }
            ]
          },
          {
            key: "assigned_to",
            label: "Assigned To",
            type: "checkbox",
            options: userOptions
          }
        ],
        onFilterChange: (filters) => {
          this.applyFilters(filters);
        }
      });

      // If we have a status filter from URL, apply it after filter manager is created
      if (this.filtersAppliedFromUrl) {
        const statusParam = this.$route.query.status;
        if (statusParam && this.filterManager) {
          this.filterManager.activeFilters = { status: [statusParam] };
          const statusFilter = this.filterManager.config.filters.find(f => f.key === 'status');
          if (statusFilter) {
            this.filterManager.updateFilterPillUI(statusFilter);
          }
        }
      }
    },

    applyFilters(activeFilters) {
      this.currentFilters = { ...activeFilters };
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetPagination() {
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetAllFilters() {
      if (this.filterManager) {
        this.filterManager.clearAllFilters();
      }
      this.currentFilters = {};
      this.sortColumn = 'schedule_at';
      this.sortOrder = 'desc';
      this.pagination.current_page = 1;
      this.fetchList();
      // Clear URL query params when resetting filters
      this.$router.replace({ query: {} });
    },

    async fetchList() {
      this.loading = true

      const payload = {
        page: this.pagination.current_page,
        ...this.currentFilters
      }

      const result = await form_api_submit({
        url: 'wo/schedule/list-all',
        method: 'GET',
        data: payload,
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.schedules = result.data.list
        this.pagination = result.data.pagination
      }

      this.loading = false
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchList()
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'fas fa-sort text-muted'
      }
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable:hover {
  background-color: #f8f9fa;
}

/* Table styling */
.table-wrapper {
  overflow-x: auto;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
}

:deep(th) {
  background-color: #f8f9fa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 1px solid #dee2e6;
}

:deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

:deep(tr:hover) {
  background-color: #f8f9fa;
}

/* Filters Wrapper */
.filters-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 8px 0;
}

.filters-wrapper .filters-bar {
  flex: 1;
}

.filters-wrapper .btn {
  white-space: nowrap;
  font-size: 12px;
  padding: 4px 12px;
}

/* Responsive */
@media (max-width: 768px) {
  :deep(th),
  :deep(td) {
    padding: 8px 12px;
  }
  
  .role-badge {
    font-size: 9px;
    padding: 1px 8px;
  }
  
  .filters-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-wrapper .btn {
    align-self: flex-end;
  }
}
</style>